import React from 'react'
import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import classnames from 'classnames'
import Typography from '../Typography'
import IconButton from '../IconButton'
import type { StyleClasses } from '../../styles'
import AlertIcon from './AlertIcon'

export interface AlertItem {
  label: string
  detail: string
}

export interface AlertProps {
  children?: React.ReactNode
  className?: string
  classes?: StyleClasses<typeof useStyles>
  content?: React.ReactNode
  title?: string
  tooltip?: string
  noMargin?: boolean
  icon?: React.ReactNode
  hideIcon?: boolean
  severity?: 'warning' | 'error' | 'info' | 'success' | 'default'
  variant?: 'default' | 'outlined' | 'filled' | 'text'
  centerIcon?: boolean
  onClose?: () => void
  /** A single-line banner. Takes precedence over `title`/`content` when set. */
  message?: React.ReactNode
  /** Top-right of the header, e.g. "Download", "Fix and retry". */
  action?: { label: string; onClick: () => void }
  /** Specific rows behind `message` — a file's skipped rows and why. Scrolls past a handful
   *  rather than growing the banner to fit. */
  items?: AlertItem[]
  /** Shorthand for `severity`, restricted to the two tones a `message` alert actually uses:
   *  `error` for something that already failed, `warning` for something that still needs a
   *  look but hasn't broken anything. Ignored if `severity` is also set. */
  tone?: 'error' | 'warning'
}

function getSeverityColor(severity: AlertProps['severity'], theme: Theme) {
  switch (severity) {
    case 'warning':
      return theme.palette.warning
    case 'error':
      return theme.palette.error
    case 'success':
      return theme.palette.success
    case 'info':
      return theme.palette.info
    default:
      return {
        light: theme.paintscout.palette.brand.Neutral.P94,
        main: theme.paintscout.palette.brand.Neutral.P70,
        dark: theme.paintscout.palette.brand.Neutral.P30,
        contrastText: '#fff'
      }
  }
}

const useStyles = makeStyles(
  (theme: Theme) => {
    return {
      root: ({ severity = 'default', variant = 'default', noMargin = false }: AlertProps) => {
        const severityColor = getSeverityColor(severity, theme)
        let background = 'transparent'
        if (variant === 'default') {
          background = severityColor.light
        } else if (variant === 'filled') {
          background = severityColor.main
        }

        const borderColor = variant === 'default' ? severityColor.light : severityColor.main

        const color = variant === 'filled' ? severityColor.contrastText : severityColor.dark

        return {
          display: 'flex',
          alignItems: 'flex-start',
          borderRadius: theme.borderRadius.md,
          padding: `${theme.spacing(2)}px ${theme.spacing(2)}px`,
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor,
          background,
          color,
          position: 'relative',
          marginBottom: noMargin ? 0 : theme.spacing(2)
        }
      },
      contentWrapper: {
        fontSize: theme.typography.body1.fontSize,
        width: '100%'
      },
      iconWrapper: ({ severity = 'default', variant = 'default' }: AlertProps) => {
        const severityColor = getSeverityColor(severity, theme)
        return {
          display: 'flex',
          alignItems: 'center',
          color: variant === 'filled' ? '#fff' : severityColor.main,
          marginTop: 2,
          marginBottom: '0px !important',
          '& svg': {
            width: 20,
            height: 20
          },
          marginRight: theme.spacing(0.5)
        }
      },
      closeWrapper: {
        position: 'absolute',
        top: 6,
        right: 0
      },
      title: {
        marginBottom: theme.spacing(0.5),
        color: 'inherit'
      },
      content: {},
      messageRow: {
        display: 'flex',
        alignItems: 'center',
        width: '100%'
      },
      action: {
        marginLeft: 'auto',
        paddingLeft: theme.spacing(2),
        flexShrink: 0,
        border: 'none',
        background: 'transparent',
        font: 'inherit',
        fontWeight: theme.typography.fontWeightMedium,
        color: 'inherit',
        textDecoration: 'underline',
        textUnderlineOffset: '2px',
        cursor: 'pointer'
      },
      items: {
        maxHeight: 180,
        overflow: 'auto',
        marginTop: theme.spacing(1)
      },
      item: {
        display: 'grid',
        gridTemplateColumns: '120px minmax(0, 1fr)',
        gap: `0 ${theme.spacing(1.5)}px`,
        alignItems: 'start',
        padding: `${theme.spacing(1)}px 0`,
        borderTop: `1px solid rgba(0, 0, 0, 0.08)`
      },
      itemLabel: {
        overflowWrap: 'anywhere'
      }
    }
  },
  {
    name: 'Alert'
  }
)

export default function Alert({
  severity,
  tone,
  variant = 'default',
  hideIcon,
  children,
  content,
  title,
  message,
  action,
  items,
  className,
  onClose,
  icon: iconProp,
  ...other
}: AlertProps) {
  // A `message` alert defaults to `error` (something already failed) absent an explicit
  // severity/tone — matching the standalone tone of the same name it was ported from. The
  // legacy title/content shape keeps its own `default` (neutral) severity.
  const resolvedSeverity = severity ?? tone ?? (message ? 'error' : 'default')
  const classes = useStyles({ severity: resolvedSeverity, variant, ...other })
  const icon = hideIcon ? null : typeof iconProp === 'undefined' ? <AlertIcon severity={resolvedSeverity} /> : iconProp

  return (
    <div className={classnames(classes.root, className)}>
      {icon && <div className={classes.iconWrapper}>{icon}</div>}
      <div className={classes.contentWrapper}>
        {title && (
          <Typography classes={{ root: classes.title }} variant={'h4'} component="p">
            {title}
          </Typography>
        )}
        {message ? (
          <>
            <div className={classes.messageRow}>
              <Typography classes={{ root: classes.content }}>{message}</Typography>
              {action && (
                <button type="button" className={classes.action} onClick={action.onClick}>
                  {action.label}
                </button>
              )}
            </div>
            {items && items.length > 0 && (
              <div className={classes.items}>
                {items.map((item, index) => (
                  <div key={index} className={classes.item}>
                    <Typography classes={{ root: classes.itemLabel }} variant="body2">
                      {item.label}
                    </Typography>
                    <Typography variant="body2">{item.detail}</Typography>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : children ? (
          <>{children}</>
        ) : (
          <AlertContent />
        )}
      </div>
      {onClose && (
        <div className={classes.closeWrapper}>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>
      )}
    </div>
  )

  function AlertContent() {
    return <>{content && <Typography classes={{ root: classes.content }}>{content}</Typography>}</>
  }
}
